import React, { useState, useRef, useEffect } from 'react';
import Editor, { useMonaco } from '@monaco-editor/react';
import { apiPost } from '../utils/apiUtils';
import { FiPlay, FiSend, FiRotateCcw } from 'react-icons/fi';
import EnhancedTestResultsPanel from './EnhancedTestResultsPanel';

const monacoLanguages = {
    'cpp': 'cpp',
    'java': 'java',
    'python': 'python',
    'javascript': 'javascript',
    'c': 'c'
};

/**
 * @file TestCodeEditorPanel.jsx
 * @description Editor panel specific to the Test/Exam interface. 
 *              Manages the Monaco editor instance, language selection, test execution (polling),
 *              and handles auto-saving of code drafts during a live test attempt.
 * @module components/TestCodeEditorPanel
 */

/* ══════════════════════════════════════════════════════════════
 * MAIN COMPONENT
 * ══════════════════════════════════════════════════════════════ */

/**
 * @component TestCodeEditorPanel
 * @description Renders the specialized coding environment for test taking.
 * 
 * @param {Object} props
 * @param {Object} props.question - The active coding question data.
 * @param {number} props.testQuestionId - The pivot ID linking the test to the question.
 * @param {number} props.submissionId - The ID of the student's current test attempt.
 * @param {number} props.testId - The ID of the overall test.
 * @param {string} props.initialCode - Boilerplate code or restored draft.
 * @param {number} props.restoredLanguageId - ID of the language the student previously used.
 * @param {Function} props.onStatusChange - Callback to bubble up grading status.
 * @param {Function} props.onPanelChange - Callback to notify parent when results panel is toggled.
 * @param {Function} props.onCodeChange - Callback to sync code changes with the parent test session.
 * @returns {JSX.Element} The rendered Test Code Editor Panel view.
 */
const TestCodeEditorPanel = ({ question, testQuestionId, submissionId, testId, initialCode, restoredLanguageId, onStatusChange, onPanelChange, onCodeChange }) => {
    const languageMap = {



        'cpp': { id: 52, name: 'C++', ext: 'cpp', logo: 'cpp.png' },
        'java': { id: 62, name: 'Java', ext: 'java', logo: 'java.png' },
        'python': { id: 71, name: 'Python', ext: 'py', logo: 'python.png' },
        'javascript': { id: 63, name: 'JavaScript', ext: 'js', logo: 'javascript.png' },
    };

    // Reverse map for restoration
    const idToLang = Object.entries(languageMap).reduce((acc, [key, val]) => ({ ...acc, [val.id]: key }), {});

    const [language, setLanguage] = useState(() => {
        if (restoredLanguageId && idToLang[restoredLanguageId]) {
            return idToLang[restoredLanguageId];
        }
        return 'cpp';
    });

    const [code, setCode] = useState(initialCode || '// Write your code here');
    const [isRunning, setIsRunning] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);


    // Panel States
    const [showResultsPanel, setShowResultsPanel] = useState(false);
    const [isResultsMinimized, setIsResultsMinimized] = useState(false);
    const [activeTab, setActiveTab] = useState('tests');
    const [testResults, setTestResults] = useState([]);
    const [submissionResult, setSubmissionResult] = useState(null);
    const [submissionProgress, setSubmissionProgress] = useState({ current: 0, total: 0, status: 'idle', passedTests: 0, failedTests: 0, message: '' });


    const editorRef = useRef(null);
    const monaco = useMonaco();

    const hasInitializedRef = useRef(false);

    /* ══════════════════════════════════════════════════════════════
     * EFFECTS & HANDLERS
     * ══════════════════════════════════════════════════════════════ */

    // Default template logic — runs ONLY ONCE on mount
    useEffect(() => {
        if (hasInitializedRef.current) return;
        hasInitializedRef.current = true;

        if (restoredLanguageId && idToLang[restoredLanguageId]) {
            setLanguage(idToLang[restoredLanguageId]);
        }

        if (initialCode) {
            setCode(initialCode);
            return;
        }

        if (question?.templates) {
            const cppTemplate = question.templates.find(t => t.language === 'cpp');
            const defaultLang = cppTemplate ? 'cpp' : (question.templates[0]?.language || 'cpp');
            if (!restoredLanguageId) {
                setLanguage(defaultLang);
            }

            const lang = restoredLanguageId ? idToLang[restoredLanguageId] : defaultLang;
            const template = question.templates.find(t => t.language === lang);
            if (template) {
                setCode(template.function_signature || '// Write your code here');
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Auto-save logic
    useEffect(() => {
        // Skip auto-save for boilerplate/initial empty states
        if (!code || code === '// Write your code here') return;

        const languageId = languageMap[language]?.id;
        if (!languageId || !submissionId || !question?.id) return;

        const timer = setTimeout(async () => {
            try {
                // Use apiRequest with PUT method for auto-saving drafts
                const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:4000/api'}/tests/${testId}/draft`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
                    },
                    body: JSON.stringify({
                        submission_id: submissionId,
                        question_id: question.id,
                        source_code: code,
                        language_id: languageId
                    })
                });

                if (!response.ok) {
                    console.warn('Auto-save failed on server');
                }
            } catch (err) {
                console.error('Auto-save system error:', err);
            }
        }, 2000); // 2 second debounce

        // Notify parent immediately for internal state sync (prevents data loss on switch)
        if (onCodeChange) onCodeChange(code);

        return () => clearTimeout(timer);
    }, [code, testId, submissionId, testQuestionId, language, onCodeChange]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest('.language-selector')) {
                // no-op: dropdown removed, but keep event listener for safety
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const onReset = () => {
        const template = question.templates?.find(t => t.language === language);
        if (template) {
            setCode(template.function_signature || '');
        }
    };

    const handleEditorDidMount = (editor, monaco) => {
        editorRef.current = editor;
        monaco.editor.setTheme('vs-dark');
    };

    const handleRunTests = async () => {
        if (isRunning || isSubmitting) return;
        if (!question?.id) {
            console.error('Cannot run tests: missing question ID');
            return;
        }
        setIsRunning(true);
        setShowResultsPanel(true);
        setIsResultsMinimized(false);
        if (onPanelChange) onPanelChange(true, false);
        setActiveTab('tests');
        setTestResults([]);

        try {
            const response = await apiPost('/run-sample', {
                languageId: languageMap[language].id,
                sourceCode: code,
                questionId: question.id
            });
            const data = await response.json();
            if (response.ok) {
                setTestResults(data.results?.map(r => ({
                    ...r,
                    passed: r.passed, // comes from processResults
                    executionTime: r.time,
                    is_test_mode_result: true
                })) || []);
                onStatusChange('attempted');
            } else {
                setTestResults([{ error: data.error || 'Failed to run tests' }]);
            }
        } catch (error) {
            setTestResults([{ error: 'Server error occurred' }]);
        } finally {
            setIsRunning(false);
        }
    };

    const handleSubmit = async () => {
        if (isRunning || isSubmitting) return;
        if (!submissionId || !testQuestionId || !question?.id) {
            console.error('Cannot submit: missing required IDs', { submissionId, testQuestionId, questionId: question?.id });
            alert('Progress synchronization issue. Please refresh the page and try again.');
            return;
        }
        setIsSubmitting(true);
        setShowResultsPanel(true);
        setIsResultsMinimized(false);
        if (onPanelChange) onPanelChange(true, false);
        setActiveTab('submission');
        setSubmissionResult(null);
        setSubmissionProgress({ current: 0, total: 0, status: 'started', passedTests: 0, failedTests: 0, message: 'Starting evaluation...' });

        try {
            // Note: Temporary measure to just use the global tracking submission endpoint which supports SSE streaming.
            const response = await apiPost('/submit', {
                userId: testId, // using testId to bypass some restrictions on backend if required, backend relies on req.user.id
                questionId: parseInt(question.id),
                testSubmissionId: submissionId,
                testQuestionId: testQuestionId,
                sourceCode: code,
                languageId: languageMap[language].id
            }, { headers: { 'Accept': 'text-event-stream', 'Cache-Control': 'no-cache' } });

            if (!response.ok) {
                const data = await response.json();
                setSubmissionResult({ status: 'Error', message: data.error || 'Failed to submit' });
                setSubmissionProgress(prev => ({ ...prev, status: 'error', message: 'Submission failed' }));
                setIsSubmitting(false);
                return;
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                let boundary = buffer.indexOf('\n\n');
                while (boundary !== -1) {
                    const message = buffer.substring(0, boundary);
                    buffer = buffer.substring(boundary + 2);

                    if (message.startsWith('data: ')) {
                        try {
                            const data = JSON.parse(message.slice(6));
                            if (data.type === 'started') {
                                setSubmissionProgress({ current: 0, total: data.totalTests, status: 'running', passedTests: 0, failedTests: 0, message: `Running ${data.totalTests} test cases...` });
                            } else if (data.type === 'test-result') {
                                setSubmissionProgress(prev => ({ ...prev, current: data.completedCount || data.testIndex, passedTests: data.passedTests || 0, failedTests: data.failedTests || 0, message: `Completed ${data.completedCount || data.testIndex} of ${prev.total} tests...` }));
                            } else if (data.type === 'execution-complete' || data.type === 'execution-stopped') {
                                const isAccepted = data.status === 'Accepted';
                                const failCount = data.failedTests || 0;
                                const passCount = data.passedTests || 0;
                                const statusMessage = isAccepted
                                    ? 'All tests passed!'
                                    : `Failed ${failCount} test case${failCount > 1 ? 's' : ''}`;
                                setSubmissionProgress(prev => ({ ...prev, current: passCount + failCount, status: isAccepted ? 'completed' : 'failed', passedTests: passCount, failedTests: failCount, message: statusMessage }));
                                setSubmissionResult({ ...data, is_test_mode_result: true });
                                onStatusChange(isAccepted ? 'Accepted' : 'attempted');
                                setIsSubmitting(false);
                            } else if (data.type === 'system-error') {
                                setSubmissionProgress(prev => ({ ...prev, status: 'error', message: 'System error occurred' }));
                                setSubmissionResult({ status: 'System Error', message: data.error || 'System error occurred' });
                                setIsSubmitting(false);
                            }
                        } catch (e) {
                            // Ignored JSON parse errors for incomplete chunks
                        }
                    }
                    boundary = buffer.indexOf('\n\n');
                }
            }
        } catch (error) {
            setSubmissionProgress(prev => ({ ...prev, status: 'error', message: 'Server error occurred' }));
            setSubmissionResult({ status: 'Error', message: 'Server error occurred' });
        } finally {
            // Safety net: always reset submitting state when stream ends
            setIsSubmitting(false);
        }
    };



    return (
        <div className="flex flex-col bg-neutral-800 h-full w-full">
            {/* Top Bar — Exact match of EnhancedCodeEditorPanel */}
            <div className="bg-neutral-800 px-2 border-b border-neutral-700">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                        <div className="flex space-x-1 hidden md:flex">
                            <div className="w-3 h-3 rounded-full bg-red-500"></div>
                            <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                        </div>
                        <span className="text-sm text-gray-400 font-mono">
                            main.{languageMap[language]?.ext}
                        </span>
                    </div>

                    <div className="flex items-center space-x-2">
                        <div className="flex items-center space-x-2">
                            <button
                                onClick={handleRunTests}
                                disabled={isRunning || isSubmitting}
                                className={`px-2 py-1 text-gray-400 hover:text-white text-sm font-medium transition-all duration-200 flex items-center space-x-1.5 rounded ${isRunning || isSubmitting ? 'bg-neutral-700 cursor-not-allowed' : ''
                                    }`}
                                title="Run Tests"
                            >
                                <FiPlay className="text-sm" />
                                <span className='hidden md:block'>Run</span>
                            </button>

                            <button
                                onClick={handleSubmit}
                                disabled={isRunning || isSubmitting}
                                className={`px-2 py-1 text-gray-400 hover:text-white text-sm font-medium transition-all duration-200 flex items-center space-x-1.5 rounded ${isRunning || isSubmitting ? 'bg-neutral-700 cursor-not-allowed' : ''
                                    }`}
                                title="Submit Code"
                            >
                                <FiSend className="text-sm" />
                                <span className='hidden md:block'>Submit</span>
                            </button>

                            <div className="h-6 w-[1px] bg-gray-400 mx-2" />

                            <button
                                onClick={onReset}
                                disabled={isRunning || isSubmitting}
                                className="p-1.5 text-gray-400 hover:text-white disabled:opacity-50 rounded transition-all duration-200"
                                title="Reset to Template"
                            >
                                <FiRotateCcw />
                            </button>

                            <div className="h-6 w-[1px] bg-gray-400 mx-2" />

                            {/* Language selector — logo-based icons matching EnhancedCodeEditorPanel */}
                            <div className="flex items-center space-x-1 language-selector">
                                {question?.templates?.map(t => {
                                    const lang = languageMap[t.language];
                                    if (!lang) return null;
                                    const isActive = language === t.language;
                                    return (
                                        <button
                                            key={t.language}
                                            onClick={() => {
                                                setLanguage(t.language);
                                                setCode(t.function_signature || '');
                                            }}
                                            disabled={isRunning || isSubmitting}
                                            className="group relative p-1.5 transition-all duration-300 rounded disabled:opacity-50"
                                            title={lang.name}
                                        >
                                            <img
                                                src={`/languages/${lang.logo}`}
                                                alt={lang.name}
                                                className={`w-8 h-8 object-contain transition-colors duration-300 ${isActive
                                                    ? 'grayscale-0 px-0.5'
                                                    : 'grayscale opacity-100 px-0.5'
                                                    }`}
                                            />
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Editor */}
            <div className={`flex-1 relative h-full font-mono`}>
                <Editor
                    height="100%"
                    theme="vs-dark"
                    language={monacoLanguages[language] || 'cpp'}
                    value={code}
                    onChange={(val) => setCode(val)}
                    onMount={handleEditorDidMount}
                    options={{
                        fontSize: 14,
                        fontFamily: 'Fira Code, Monaco, Menlo, "Ubuntu Mono", monospace',
                        fontLigatures: true,
                        minimap: { enabled: false },
                        scrollBeyondLastLine: false,
                        wordWrap: 'on',
                        automaticLayout: true,
                        lineNumbers: 'on',
                        folding: false,
                        lineNumbersMinChars: 3,
                        lineDecorationsWidth: 16,
                        renderLineHighlight: 'all',
                        selectOnLineNumbers: true,
                        bracketPairColorization: { enabled: true },
                        guides: { bracketPairs: false, indentation: true },
                        padding: { top: 16 },
                        contextmenu: false,
                        mouseWheelZoom: true,
                        glyphMargin: false
                    }}
                    loading={
                        <div className="flex items-center justify-center h-full">
                            <div className="text-center">
                                <div className="w-8 h-8 border-4 border-emerald-400 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                                <p className="text-gray-400 text-sm">Loading Editor...</p>
                            </div>
                        </div>
                    }
                />
            </div>

            {/* Test Results Panel */}
            {showResultsPanel && (
                <div className={`fixed bottom-0 left-[256px] right-0 z-40 ${isResultsMinimized ? 'h-12' : 'h-[40vh]'} transition-all duration-300`}>
                    <EnhancedTestResultsPanel
                        testResults={testResults}
                        submissionResult={submissionResult}
                        submissionProgress={submissionProgress}
                        isRunning={isRunning}
                        isSubmitting={isSubmitting}
                        isMinimized={isResultsMinimized}
                        onMinimize={() => {
                            setIsResultsMinimized(!isResultsMinimized);
                            if (onPanelChange) onPanelChange(true, !isResultsMinimized);
                        }}
                        onClose={() => {
                            setShowResultsPanel(false);
                            if (onPanelChange) onPanelChange(false, false);
                            setTestResults([]);
                            setSubmissionResult(null);
                        }}
                        activeTab={activeTab}
                        setActiveTab={setActiveTab}
                    />
                </div>
            )}
        </div>
    );
};

export default TestCodeEditorPanel;
